import { useState } from 'react'
import { useUser, useClerk } from '@clerk/clerk-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { LogOut, Trash2 } from 'lucide-react'

import { api } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { ApiResponse } from '@/types/api'
import type { NotificationPreferences } from '@/types/models'

// ---------------------------------------------------------------------------
// ToggleRow
// ---------------------------------------------------------------------------

interface ToggleRowProps {
  label: string
  description?: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

function ToggleRow({ label, description, checked, onChange, disabled }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div className="flex-1 min-w-0 pr-4">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 ${
          checked ? 'bg-brand-blue' : 'bg-muted'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// SettingsPage
// ---------------------------------------------------------------------------

/**
 * SettingsPage — notification preferences and account management.
 *
 * Notification toggles call PATCH /api/v1/notifications/preferences on change.
 * Account section shows Clerk user info and sign-out.
 */
export default function SettingsPage() {
  const { user } = useUser()
  const { signOut } = useClerk()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentWorkspaceId } = useWorkspaceStore()

  // Fetch notification preferences
  const { data: prefsData, isLoading } = useQuery<ApiResponse<NotificationPreferences>>({
    queryKey: ['notification-preferences', currentWorkspaceId],
    queryFn: () =>
      api.get<ApiResponse<NotificationPreferences>>('/v1/notifications/preferences'),
    enabled: !!currentWorkspaceId,
  })
  const prefs = prefsData?.data

  const patchPrefsMutation = useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) =>
      api.patch('/v1/notifications/preferences', patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({
        queryKey: ['notification-preferences', currentWorkspaceId],
      })
      const previous = queryClient.getQueryData(['notification-preferences', currentWorkspaceId])
      queryClient.setQueryData<ApiResponse<NotificationPreferences>>(
        ['notification-preferences', currentWorkspaceId],
        (old) =>
          old && old.data ? { ...old, data: { ...old.data, ...patch } } : old,
      )
      return { previous }
    },
    onError: (_err, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          ['notification-preferences', currentWorkspaceId],
          context.previous,
        )
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['notification-preferences', currentWorkspaceId],
      })
    },
  })

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)

  const resetWorkspace = useWorkspaceStore((s) => s.reset)

  async function handleSignOut() {
    resetWorkspace()
    await signOut()
    navigate('/sign-in')
  }

  async function handleDeleteAccount() {
    if (!user) return
    setIsDeleting(true)
    setDeleteError('')
    try {
      resetWorkspace()
      await user.delete()
      navigate('/sign-in')
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account.')
      setIsDeleting(false)
    }
  }

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-semibold text-foreground mb-8">Settings</h1>

      {/* Notifications preferences */}
      <section className="mb-8">
        <h2 className="text-base font-semibold text-foreground mb-1">
          Notification Preferences
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Choose how and when you receive notifications.
        </p>

        {isLoading && (
          <div className="text-sm text-muted-foreground py-4">Loading preferences...</div>
        )}

        {prefs && (
          <div className="rounded-xl border bg-card px-4">
            <p className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Email Notifications
            </p>
            <ToggleRow
              label="Deadline Reminders"
              description="Email alerts when application deadlines are approaching"
              checked={prefs.emailDeadlineReminders}
              onChange={(v) => patchPrefsMutation.mutate({ emailDeadlineReminders: v })}
              disabled={patchPrefsMutation.isPending}
            />
            <ToggleRow
              label="LOR Status Changes"
              description="Email when a recommender updates their letter status"
              checked={prefs.emailLorStatusChanges}
              onChange={(v) => patchPrefsMutation.mutate({ emailLorStatusChanges: v })}
              disabled={patchPrefsMutation.isPending}
            />
            <p className="pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              In-App Notifications
            </p>
            <ToggleRow
              label="Deadline Reminders"
              description="In-app alerts for upcoming application deadlines"
              checked={prefs.inAppDeadlineReminders}
              onChange={(v) => patchPrefsMutation.mutate({ inAppDeadlineReminders: v })}
              disabled={patchPrefsMutation.isPending}
            />
            <ToggleRow
              label="LOR Status Changes"
              description="In-app notifications when recommender status changes"
              checked={prefs.inAppLorStatusChanges}
              onChange={(v) => patchPrefsMutation.mutate({ inAppLorStatusChanges: v })}
              disabled={patchPrefsMutation.isPending}
            />
          </div>
        )}
      </section>

      {/* Account */}
      <section>
        <h2 className="text-base font-semibold text-foreground mb-1">Account</h2>
        <p className="text-sm text-muted-foreground mb-4">Manage your account and session.</p>

        <div className="rounded-xl border bg-card px-4 py-4">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-purple/20">
              {user?.imageUrl ? (
                <img
                  src={user.imageUrl}
                  alt={user.fullName ?? 'User avatar'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-base font-semibold text-brand-purple">
                  {(
                    user?.firstName?.[0] ??
                    user?.emailAddresses?.[0]?.emailAddress?.[0] ??
                    'U'
                  ).toUpperCase()}
                </span>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-foreground truncate">
                {user?.fullName ?? 'User'}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {user?.primaryEmailAddress?.emailAddress ?? ''}
              </p>
            </div>
          </div>

          <div className="mt-4 border-t pt-4 flex flex-col gap-3">
            <button
              type="button"
              onClick={handleSignOut}
              className="flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring w-fit"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>

            {!showDeleteConfirm ? (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 rounded-md border border-destructive px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive w-fit"
              >
                <Trash2 className="h-4 w-4" />
                Delete Account
              </button>
            ) : (
              <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 space-y-3">
                <p className="text-sm font-semibold text-destructive">Are you sure?</p>
                <p className="text-sm text-muted-foreground">
                  This permanently deletes your account and all data — applications, documents, cycles, and recommendations. This cannot be undone.
                </p>
                {deleteError && (
                  <p className="text-xs text-destructive">{deleteError}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={isDeleting}
                    className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
                  >
                    {isDeleting ? 'Deleting…' : 'Yes, delete my account'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowDeleteConfirm(false); setDeleteError('') }}
                    disabled={isDeleting}
                    className="rounded-md border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
